import { uniq } from 'es-toolkit';
import React from 'react';
import { Autocomplete, TextField } from '@mui/material';
import { createFilterOptions } from '@mui/material/useAutocomplete';

import { INDETERMINATE_TEXT } from '@paragon/client-tools';
import { enqueueError } from '@paragon/notification-tools';

import { useAppDispatch } from '../../../hooks';

type Option = number | string;
const filter = createFilterOptions<Option>();

interface Props {
  readonly deflectionLimitValues: readonly number[];
  readonly setDeflectionLimitValue: (value: number) => void;
  readonly deflectionLimitOptions: readonly number[];
  readonly disabled?: boolean;
  readonly size?: 'small' | 'medium' | undefined;
  readonly label?: string;
}

// Inspired by https://v4.mui.com/components/autocomplete/#creatable
export default function DeflectionLimitSelect(props: Props) {
  const dispatch = useAppDispatch();
  const { deflectionLimitValues, setDeflectionLimitValue, deflectionLimitOptions, disabled, size, label } = props;
  const uniqueDeflectionLimitValues = uniq(deflectionLimitValues);

  return (
    <Autocomplete<Option, false, false, true>
      value={uniqueDeflectionLimitValues.length > 1 ? INDETERMINATE_TEXT : uniqueDeflectionLimitValues[0]}
      onChange={(_, newValue) => {
        if (typeof newValue === 'string') {
          const number = parseFloat(newValue);
          if (Number.isNaN(number)) {
            dispatch(enqueueError('Deflection limits must be numbers'));
            return;
          }
          setDeflectionLimitValue(number);
        } else if (newValue != null) {
          setDeflectionLimitValue(newValue);
        }
      }}
      filterOptions={(options, params) => {
        const filtered = filter(options, params);

        // Suggest the creation of a new value
        if (params.inputValue !== '') {
          filtered.push(params.inputValue);
        }

        return filtered;
      }}
      selectOnFocus
      clearOnBlur
      handleHomeEndKeys
      options={deflectionLimitOptions}
      getOptionLabel={(option) => {
        // Created value
        if (typeof option === 'string') {
          return option;
        }
        // Regular option
        return `${option}`;
      }}
      freeSolo
      renderInput={(params) => <TextField label={label} variant="outlined" {...params} />}
      disabled={disabled}
      size={size ?? 'small'}
    />
  );
}
